import { transformDateToLocalDateString, transformRoute } from "helpers";
import { FleetDocumentRow, FleetDocumentVehicleRef } from "services/api/fleet/vehicles/documents/types";
import { FleetVehicle } from "services/api/fleet/vehicles/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { PictureIcon } from "ui/components/Pictures";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";

// VehicleCell strogo želi FleetVehicle s puno polja koja nemamo na slim ref-u
// (ovaj endpoint vraća samo id / nazive / plate). VehicleCell sva neimena polja
// čita kroz optional chaining, pa adapter samo popunjava ono što imamo i radi
// kontroliran cast — alternativa bi bila slati težu Vehicle DTO sa svakim
// dokumentom samo radi tooltipa.
const adaptVehicleRefForCell = (v: FleetDocumentVehicleRef): FleetVehicle =>
  ({
    id: v.id,
    defaultImage: v.image ?? undefined,
    vehicleManufacturer: { name: v.manufacturerName },
    vehicleModel: { name: v.modelName },
    vehicleType: { name: v.typeName },
    registrationPlate: v.registrationPlate,
  } as unknown as FleetVehicle);

export const FINANCE_DOCUMENT_SEARCH_COLUMNS = (
  t: (key: string) => string,
  onDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  onVehicleClick: (event: React.MouseEvent, vehicleId: string) => void
): Column[] => {
  return [
    {
      title: "",
      field: "image",
      type: ColumnTypes.custom,
      render: (row: FleetDocumentRow) => {
        const uploads = row.childRows ?? [];

        // Bez priloga → fallback inicijal tipa dokumenta (kao i na vehicle searchu).
        if (uploads.length === 0) {
          return (
            <div className="w-10 h-10 rounded-full bg-gray-200 block relative">
              <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                {row.documentType?.name.toString().charAt(0).toUpperCase()}
              </span>
            </div>
          );
        }

        return (
          <PictureIcon
            maxVisible={3}
            items={uploads.map((u) => ({
              id: u.id,
              url: u.url,
              fileName: u.originalFileName,
              tooltip: u.originalFileName,
            }))}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.document.search.name",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetDocumentRow) => {
        return (
          <div>
            <div className="text-md">{row.name}</div>
            <div className="text-sm font-light text-slate-500">
              {row.documentType?.name}
            </div>
          </div>
        );
      },
    },
    {
      title: "Vozilo",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetDocumentRow) => {
        if (!row.vehicle) return null;
        return (
          <VehicleCell
            vehicle={adaptVehicleRefForCell(row.vehicle)}
            onClick={(e) => onVehicleClick(e, row.vehicle!.id)}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.document.search.description",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetDocumentRow) => (
        <div className="text-sm font-light text-slate-500">
          {row.description}
        </div>
      ),
    },
    {
      title: "admin.fleet.vehicle.document.search.createdon",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetDocumentRow) => (
        <div className="text-md">
          {transformDateToLocalDateString(row.created)}
        </div>
      ),
    },
    {
      title: "",
      key: "document_actions",
      alwaysVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetDocumentRow) => {
        return (
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => onDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ];
};

// Helper za caller — vraća rute za navigaciju na vehicle detail. Drži se ovdje
// uz consts tako da se ne ponavlja po pozivateljima.
export const buildVehicleDetailRoute = (vehicleId: string): string =>
  transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
    id: vehicleId,
  });
